// janus-engine/movement/inventory-support.ts — Force Move S1 (2026-09-28): the INVENTORY-TOP support of check_and_apply_falling()
// step 5 (includes/functions/janus/encounter/check_and_apply_falling.php), for the shared fall twin (fall-check.ts).
//
// Membership shares the scene/movement solidity predicate; cover opt-outs do not remove physical support.
// The highest overlapping top supports the mover. The existing x-only overlap and picture-height geometry
// remain shared with the non-mesh fall readers below.
import { SIZE_LADDER } from '../inventory/transform-edit.js';
import { inventorySceneryShell, inventoryEncounterLink, inventoryEquipmentSlot, inventoryFixtureFlags, inventoryPropName, inventorySolid } from '../inventory/solid-obstacle.js';
import { inventoryCatalog, inventoryDefinition } from '../inventory/definition.js';
import { inventoryMeshDrawn } from '../inventory/prop-mesh-space.js';
import { readInventoryField } from '../compile/inventory-raw-readers.js';
import { itemsCol, posCoord } from '../../shared/array-types.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TINY_SIZE_ID = '13456FEC-A2EE-DB46-5829-0EB2F627E1B7';
/** TRANSITION_NAV_PROP_ITEM_IDS (inventory_is_transition_navigation_prop.php). */
const TRANSITION_NAV_PROP_ITEM_IDS = ['9A88314E-7EDA-71C8-C98D-04DE57894484', 'ABE07D2F-7E8D-E61E-BCEE-AD1C2435C9D8', 'BF4DEA68-3856-F56D-7C6F-082BCA0E5450'];

export interface InventorySupportReaders {
  /** layer_value() over ONE held row as it stands (ftLayerValue) — never a hydration. */
  value: (row: Row, name: string) => unknown;
  /** The encounter root catalog's definition for an item id, or null. */
  definition: (id: string) => Row | null;
}

/** Lean position buckets elide zero axes. A missing bucket is unplaced, never the origin. */
export function heldMovementPosition(row: Row): [number, number, number] {
  const position = record(row.position) ?? record(row.pos_arr) ?? (['x_pos', 'y_pos', 'z_pos'].some(key => key in row) ? row : null);
  return (['x_pos', 'y_pos', 'z_pos'] as const).map(axis => !position ? NaN
    : posCoord(row, axis) ?? (position[axis] == null ? 0 : NaN)) as [number, number, number];
}

export function inventoryHasPosition(row: Row, position = heldMovementPosition): boolean {
  return position(row).every(Number.isFinite);
}

/** PHP empty(). */
const phpEmpty = (value: unknown): boolean => {
  const v = scalar(value);
  return v === undefined || v === null || v === '' || v === '0' || v === 0 || v === false || (Array.isArray(value) && value.length === 0);
};
const bucket = (row: Row, key: string): Row => record(row[key]) ?? {};
const inventoryItemId = (inv: Row): string => upper(bucket(inv, 'inventory').inventory_item_id ?? bucket(inv, 'items').item_id);
const template = (inv: Row, read: InventorySupportReaders): Row | null => inventoryDefinition(inv, null, read.definition);

/** ft_inventory_template_layer_value(): the row's own layer when it carries one, else its template's — including a row that
 *  carries other own layers (rubble: size / weight / composition), whose compiled value for a layer it lacks is the template's
 *  (oracle: 80 / 80 rubble rows). `undefined` = no template held: the page cannot answer. */
export function inventoryTemplateValue(inv: Row, name: string, read: InventorySupportReaders): unknown {
  const own = record(inv.item_layer_types);
  if (own && name in own) return read.value(inv, name);
  const def = template(inv, read);
  return def ? read.value(def, name) ?? null : undefined;
}

/** get_base_size() for an inventory row: its size's base, Tiny when it names none (a real inventory row), 5 ft when unknown. */
export function inventoryBaseSizeFt(inv: Row, read: InventorySupportReaders): number | undefined {
  const size = inventoryTemplateValue(inv, 'size', read);
  if (size === undefined) return undefined;
  const invId = String(scalar(bucket(inv, 'inventory').inventory_id) ?? '');
  const sizeId = phpEmpty(size) ? (UUID.test(invId) ? TINY_SIZE_ID : null) : upper(size);
  if (sizeId === null) return 5;
  return SIZE_LADDER.find(entry => entry.id === sizeId)?.baseSize ?? 5;
}

/** get_base_size() for a creature row (the mover). */
export function creatureBaseSizeFt(row: Row, read: InventorySupportReaders): number {
  const size = read.value(row, 'size');
  if (phpEmpty(size)) return 5;
  return SIZE_LADDER.find(entry => entry.id === upper(size))?.baseSize ?? 5;
}

/** inventory_is_transition_navigation_prop(). */
export function isTransitionNavigationProp(inv: Row, definition: Row | null = null): boolean {
  if (TRANSITION_NAV_PROP_ITEM_IDS.includes(inventoryItemId(inv))) return true;
  const name = inventoryPropName(inv, definition).toLowerCase();
  return name.includes('stair') || name.includes('ladder') || name.includes('ramp');
}

const galleries = (host: Row | null, type: string): Row[] => {
  if (!host) return [];
  const own = record(host.pictures)?.[type];
  if (Array.isArray(own) && own.length) return own.map(record).filter((r): r is Row => r !== null);
  const legacy = type === 'item' ? (host.item_pictures ?? host.item_picture) : host[type];
  return Array.isArray(legacy) ? legacy.map(record).filter((r): r is Row => r !== null) : [];
};
const pictureById = (hosts: Array<Row | null>, id: string, types: string[]): Row | null => {
  const want = id.toLowerCase();
  for (const host of hosts) for (const type of types) for (const pic of galleries(host, type)) {
    if (String(scalar(pic.picture_id) ?? '').toLowerCase() === want) return pic;
  }
  return null;
};

/** get_object_picture_arr()'s inventory branch over the thin row + its template (the compiled row's galleries are the
 *  template's plus the instance's own). */
export function inventoryPicture(inv: Row, read: InventorySupportReaders): Row | null {
  const def = template(inv, read);
  const quick = scalar(inv.pic_url);
  if (typeof quick === 'string' && quick !== '') return { picture_url: [quick] };
  const instance = galleries(inv, 'inventory_picture');
  if (instance.length) return instance[0]!;
  const presentation = String(scalar(bucket(inv, 'inventory').inventory_presentation_pic_id) ?? '');
  if (bucket(inv, 'inventory').inventory_id && UUID.test(presentation)) {
    const found = pictureById([inv, def], presentation, ['inventory_presentation_picture', 'inventory_picture', 'item']);
    if (found) return found;
  }
  const items = galleries(inv, 'item');
  if (items.length) return items[0]!;
  return galleries(def, 'item')[0] ?? null;
}

/** get_object_height_feet() for an inventory row; undefined = not answerable from held data. */
export function inventoryHeightFt(inv: Row, read: InventorySupportReaders): number | undefined {
  const base = inventoryBaseSizeFt(inv, read);
  if (base === undefined) return undefined;
  const picture = inventoryPicture(inv, read);
  // No picture on the row AND no template held: the template's gallery could have supplied a non-square one (Codex review).
  if (!picture) return template(inv, read) ? base : undefined;
  const dim = (key: string): number => {
    const raw = picture[key];
    const v = Array.isArray(raw) ? raw[0] : raw;
    const n = typeof v === 'number' ? v : typeof v === 'string' ? parseInt(v, 10) : 0;
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  };
  const width = dim('picture_width'), height = dim('picture_height');
  if (width <= 0 || height <= 0) return base;
  return Math.round(base * (height / width) * 100) / 100;
}

/** has_substantial_horizontal_overlap(): x-only, ≥ threshold of the smaller base. */
export function substantialXOverlap(x1: number, base1: number, x2: number, base2: number, threshold = 0.33): boolean {
  const overlap = Math.max(0, Math.min(x1 + base1 / 2, x2 + base2 / 2) - Math.max(x1 - base1 / 2, x2 - base2 / 2));
  return overlap >= threshold * Math.min(base1, base2);
}

export interface InventorySupportInput {
  /** encounter_inventories_arr as the page holds it. */
  inventories: Row[];
  /** The mover's x and get_base_size(). */
  moverX: number;
  moverBase: number;
  /** Feet (authored floor) / bottom, and the size-tier grace. */
  bottom: number;
  grace: number;
  /** ft_encounter_shape() present: tops are z + height (feet-based), else z + height / 2. */
  authored: boolean;
  /** Death/dying's legacy sweep includes transition props; ordinary falling skips them. */
  skipTransitions?: boolean;
}
export type InventorySupport = { top: number | null; supporters: string[] } | { unknown: string; row: string };

/** check_and_apply_falling() step 5's inventory loop: the highest supporting top (null = none). */
export function inventorySupport(input: InventorySupportInput, read: InventorySupportReaders): InventorySupport {
  let top: number | null = null;
  const supporters: string[] = [];
  for (const inv of input.inventories) {
    const id = String(readInventoryField(inv, 'inventory_id') ?? '');
    if (Number(readInventoryField(inv, 'inventory_deleted') ?? 0) === 1 || !inventoryHasPosition(inv)) continue;
    const definition = inventoryDefinition(inv, null, read.definition);
    if (!definition) return { unknown: `prop-template-not-held:${inventoryItemId(inv)}`, row: id };
    let generation = readInventoryField(inv, 'inventory_generation');
    if (typeof generation === 'string') { try { generation = JSON.parse(generation); } catch { generation = null; } }
    const itemType = String(itemsCol(inv, 'item_type') ?? itemsCol(definition, 'item_type') ?? '');
    if (!inventorySolid({ sceneryShell: inventorySceneryShell(inv), private: Number(readInventoryField(inv, 'inventory_private') ?? 0) !== 0,
      itemType, name: inventoryPropName(inv, definition), equipmentSlot: inventoryEquipmentSlot(inv, definition),
      quantity: Number(readInventoryField(inv, 'item_qty') ?? 1), encounterLink: inventoryEncounterLink(inv),
      wallRun: !!record(generation)?.wallRun, corpseLinked: !!readInventoryField(inv, 'inventory_encounter_creature_id'),
      ...inventoryFixtureFlags(key => inventoryTemplateValue(inv, key, read)),
      meshDrawn: inventoryMeshDrawn(inv, definition, inventoryCatalog(inv, null, definition)) })) continue;
    if (input.skipTransitions !== false && isTransitionNavigationProp(inv, definition)) continue;
    const base = inventoryBaseSizeFt(inv, read);
    if (base === undefined) return { unknown: 'size', row: id };
    const [x, , z] = heldMovementPosition(inv);
    if (!substantialXOverlap(input.moverX, input.moverBase, x, base)) continue;
    const height = inventoryHeightFt(inv, read);
    if (height === undefined) return { unknown: 'height', row: id };
    const rowTop = z + (input.authored ? height : height / 2);
    if (rowTop >= input.bottom + input.grace) continue;
    supporters.push(id);
    if (top === null || rowTop > top) top = rowTop;
  }
  return { top, supporters };
}
